import { memo, useEffect } from 'react';
import { X } from 'lucide-react';
import { useFocusTrap } from '@/hooks/useFocusTrap';

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutGroup {
  title: string;
  shortcuts: { keys: string[]; description: string }[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['g', 'd'], description: 'Go to Dashboard' },
      { keys: ['g', 's'], description: 'Go to Subscribers' },
      { keys: ['g', 'b'], description: 'Go to Billing' },
      { keys: ['g', 'p'], description: 'Go to Plans' },
      { keys: ['g', 't'], description: 'Go to Tickets' },
      { keys: ['g', 'o'], description: 'Go to OLT' },
      { keys: ['g', 'n'], description: 'Go to NAS' },
      { keys: ['g', 'c'], description: 'Go to NOC' },
      { keys: ['g', 'r'], description: 'Go to Reports' },
      { keys: ['g', 'l'], description: 'Go to Logs' },
      { keys: ['g', 'e'], description: 'Go to Settings' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['n'], description: 'New item (context-aware)' },
      { keys: ['/'], description: 'Focus search' },
      { keys: ['⌘', 'K'], description: 'Command palette' },
    ],
  },
  {
    title: 'General',
    shortcuts: [
      { keys: ['?'], description: 'Show this help' },
      { keys: ['Esc'], description: 'Close dialog / modal' },
    ],
  },
];

export const KeyboardShortcutsHelp = memo(function KeyboardShortcutsHelp({
  open,
  onClose,
}: KeyboardShortcutsHelpProps) {
  const trapRef = useFocusTrap<HTMLDivElement>(open);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-x-0 top-[10%] z-50 mx-auto w-full max-w-lg px-4">
        <div ref={trapRef} role="dialog" aria-modal="true" aria-labelledby="shortcuts-title" className="animate-dialog-in overflow-hidden rounded-2xl border bg-card shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 id="shortcuts-title" className="text-base font-semibold text-foreground">Keyboard Shortcuts</h2>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Shortcut groups */}
          <div className="max-h-[60vh] overflow-y-auto p-6 space-y-6">
            {SHORTCUT_GROUPS.map((group) => (
              <div key={group.title}>
                <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.title}
                </h3>
                <div className="space-y-2">
                  {group.shortcuts.map((shortcut) => (
                    <div
                      key={shortcut.description}
                      className="flex items-center justify-between rounded-lg px-2 py-1.5"
                    >
                      <span className="text-sm text-foreground">{shortcut.description}</span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, i) => (
                          <span key={i}>
                            {i > 0 && shortcut.keys.length > 1 && key.length === 1 && (
                              <span className="mx-0.5 text-xs text-muted-foreground">then</span>
                            )}
                            <kbd className="inline-flex min-w-[24px] items-center justify-center rounded-md border bg-muted px-1.5 py-0.5 font-mono text-[11px] font-medium text-muted-foreground shadow-sm">
                              {key}
                            </kbd>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-3">
            <p className="text-center text-[11px] text-muted-foreground">
              Press <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">?</kbd> anytime to toggle this help
            </p>
          </div>
        </div>
      </div>
    </>
  );
});
